import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'antd-mobile';
import { FrameAnimation } from 'common-package';
import { useInterval, useLongPress } from 'ahooks';
import { Icon } from '@fuxi/eevee-icon';
import { AnimatePresence, motion } from 'framer-motion';
import { SocketClientType } from '@/service/socketio';
import { ServiceProcessCrumb, SuggestionsForFeedback, Toast } from '@/components';
import dashedCircleImg from '@/assets/image/dashed-circle.png';
import dashedLineImg from '@/assets/image/dashed-line.png';
import { storagePrefixFixedV2, storagePrefixV2 } from '@/constant';
import { MassageControlValueContext } from '../index';

import cx from './index.module.scss';

enum MACHINE_TYPE_IMAGE {
  // 倍轻松艾灸按摩头
  Moxibustion = storagePrefixV2 + '/machine.png',
  // 射频机器
  RadioFrequency = storagePrefixFixedV2 + '/installation/machine-first-frame.png',
  // 冲击波按摩头
  Shockwave = storagePrefixFixedV2 + '/installation/machine-first-frame.png',
  // 通用按摩头
  Massage = storagePrefixV2 + '/machine.png',
  // 热砭石
  HotStone = storagePrefixFixedV2 + '/installation/machine-first-frame.png',
  // 矩阵艾灸头
  MatrixMoxibustion = storagePrefixV2 + '/massage-head-move/matrix-moxibustion-machine-2-min.png',
}

enum MASSAGE_HEAD_TYPE_IMAGE {
  // 倍轻松艾灸按摩头
  Moxibustion = storagePrefixV2 + '/massage-head-move/moxibustion-head.png',
  // 射频机器
  RadioFrequency = storagePrefixV2 + '/massage-head-move/radio-frequency-head.png',
  // 冲击波按摩头
  Shockwave = storagePrefixV2 + '/massage-head-move/shockwave-head.png',
  // 通用按摩头
  Massage = storagePrefixV2 + '/massage-head/massage-head-new.png',
  // 热砭石
  HotStone = storagePrefixV2 + '/massage-head/hot-stone.png',
  // 矩阵艾灸头
  MatrixMoxibustion = storagePrefixV2 + '/massage-head-move/mox-head-min.png',
}
// 末端固定走线动画
enum WIRE_MOVIE {
  RadioFrequency = storagePrefixFixedV2 + '/installation/install-wire-image/radio-frequency.png',
  Shockwave = storagePrefixFixedV2 + '/installation/install-wire-image/shockwave-new.png',
  HotStone = storagePrefixFixedV2 + '/installation/install-wire-image/hostone-new.png',
}

interface Props {
  currentTreatType: Massage.MassageItem | 'MatrixMoxibustion';
  currentProcess: Service.SubStep;
  isDisconnectMachineArm: boolean;
  socketInstance: React.MutableRefObject<SocketClientType>;
  isStoping: boolean;
  endService: () => Promise<boolean>;
}

const InstallMassageHead: React.FC<Props> = props => {
  const { currentTreatType, socketInstance, endService, isStoping, currentProcess, isDisconnectMachineArm } =
    props || {};

  const [isCompletingHead, setIsCompletingHead] = useState<boolean>(false);
  const [isCompletingWire, setIsCompletingWire] = useState<boolean>(false);

  const [massageState, setMassageState] = useState<RobotState>(null);
  const { isSocketConnected } = useContext(MassageControlValueContext);
  const endBtnRef = useRef(null);
  const confirmBtn = useRef(null);

  const confirmBtnDisabled = useMemo(() => {
    return (
      isDisconnectMachineArm ||
      (massageState === 'Preparing_InstallingWiring' ? false : massageState !== 'Preparing_WaitingInstallation')
    );
  }, [isDisconnectMachineArm, massageState]);

  const confirmBtnLoading = useMemo(() => {
    return (massageState === 'Preparing_InstallingWiring' ? isCompletingWire : isCompletingHead) || isStoping;
  }, [isCompletingHead, isCompletingWire, isStoping, massageState]);

  const confirmInstallMassageHead = useCallback(async () => {
    if (isDisconnectMachineArm || massageState !== 'Preparing_WaitingInstallation' || isCompletingHead || isStoping) {
      return;
    }

    if (!isSocketConnected) {
      Toast.show('无法连接到机器人');
      return;
    }

    setIsCompletingHead(true);
    try {
      await socketInstance.current.completeInstallation();
      // 兼容有时候不会获取最新的机器状态导致按钮一直loading
      const isSpecialType = ['Shockwave', 'RadioFrequency', 'HotStone']?.includes(currentTreatType);
      if (isSpecialType) {
        const state = await socketInstance.current.getMachineState();
        setMassageState(state?.state);
      }
    } catch {
      setIsCompletingHead(false);
    }
  }, [
    currentTreatType,
    isCompletingHead,
    isDisconnectMachineArm,
    isSocketConnected,
    isStoping,
    massageState,
    socketInstance,
  ]);

  const confirmInstallMassageWire = useCallback(async () => {
    if (isDisconnectMachineArm || massageState !== 'Preparing_InstallingWiring' || isCompletingWire || isStoping) {
      return;
    }

    if (!isSocketConnected) {
      Toast.show('无法连接到机器人');
      return;
    }

    setIsCompletingWire(true);
    try {
      await socketInstance.current.completeWireInstallation();
    } catch {
      setIsCompletingWire(false);
    }
  }, [isCompletingWire, isDisconnectMachineArm, isSocketConnected, isStoping, massageState, socketInstance]);

  const confirmInstall = useCallback(() => {
    if (massageState === 'Preparing_InstallingWiring') {
      confirmInstallMassageWire();
    } else {
      confirmInstallMassageHead();
    }
  }, [confirmInstallMassageHead, confirmInstallMassageWire, massageState]);

  const clearLoop = useInterval(
    () => {
      loopGetState();
    },
    1000,
    { immediate: true }
  );

  const loopGetState = useCallback(async () => {
    const state = await socketInstance.current.getMachineState();
    setMassageState(state?.state);
    // 磁震波/内源热能/热砭石类型进入固定末端走线
    // 统一处理停止条件，避免分支遗漏
    const isSpecialType = ['Shockwave', 'RadioFrequency', 'HotStone']?.includes(currentTreatType);
    const isStopCondition =
      (isSpecialType && state?.state === 'Preparing_InstallingWiring') ||
      (!isSpecialType && state?.state === 'Preparing_WaitingInstallation');

    if (isStopCondition) {
      clearLoop();
    }
  }, [clearLoop, currentTreatType, socketInstance]);

  // 是否有涂抹介质的提示
  const hasTipsForWipeMedium = useMemo(
    () => ['Shockwave', 'RadioFrequency', 'HotStone'].includes(currentTreatType),
    [currentTreatType]
  );

  const controlTriggerEnd = useCallback(() => {
    if (isDisconnectMachineArm || isStoping || massageState === 'Finishing') {
      return;
    }
    endService?.();
  }, [endService, isDisconnectMachineArm, isStoping, massageState]);

  /** 结束 */
  useLongPress(() => {}, endBtnRef.current?.nativeElement, {
    onClick: controlTriggerEnd,
    onLongPressEnd: controlTriggerEnd,
  });

  /** 确认安装理疗末端/确认固定末端走线 */
  useLongPress(() => {}, confirmBtn.current?.nativeElement, {
    onClick: confirmInstall,
    onLongPressEnd: confirmInstall,
  });

  useEffect(() => {
    if (isDisconnectMachineArm) {
      setIsCompletingHead(false);
    }
  }, [isDisconnectMachineArm]);

  return (
    <div
      className={cx(
        'massage-device',
        { 'not-show': currentTreatType === null },
        { 'others-device': ['Moxibustion', 'Massage']?.includes(currentTreatType) },
        { 'matrix-moxibustion-device': currentTreatType === 'MatrixMoxibustion' }
      )}
      style={{
        backgroundImage: ['Shockwave', 'RadioFrequency', 'HotStone']?.includes(currentTreatType)
          ? currentProcess === 'Installation' && massageState !== 'Preparing_InstallingWiring'
            ? `url(${MACHINE_TYPE_IMAGE[currentTreatType] || ''})`
            : 'none'
          : `url(${MACHINE_TYPE_IMAGE[currentTreatType] || ''})`,
      }}
    >
      {currentProcess !== 'Massage' && <ServiceProcessCrumb currentPageStep="Installation" />}

      <div
        className={cx('animation-wrapper', {
          show: currentProcess === 'Installation' && massageState === 'Preparing_InstallingWiring',
        })}
      >
        <FrameAnimation
          className={cx('fix-end-route')}
          url={WIRE_MOVIE[currentTreatType]}
          aspectRatio={1.2755}
          count={41}
          duration={2.5}
          loopTimes={1}
          width={window.innerWidth > 1300 ? 824 : 805}
          isPlay={currentProcess === 'Installation' && massageState === 'Preparing_InstallingWiring'}
        />
      </div>

      <div
        className={cx('massage-head-type', currentTreatType, {
          show: currentProcess === 'Installation' && massageState !== 'Preparing_InstallingWiring',
        })}
        style={{
          backgroundImage: `url(${MASSAGE_HEAD_TYPE_IMAGE[currentTreatType] || ''})`,
        }}
      />
      <AnimatePresence mode="wait">
        {massageState === 'Preparing_InstallingWiring' ? (
          <motion.div className={cx('animate-card')}>
            <motion.div
              key="fix-end-wire"
              className="card"
              initial={{ opacity: 0, right: '-5%', top: '2.5%', zIndex: 1000 }}
              animate={{ opacity: 1, right: 0, top: '2.5%', zIndex: 1000 }}
              transition={{ duration: 1, delay: 0.4 }}
            >
              <div className={cx('tips-box')}>
                <div className={cx('tips-text')}>
                  <div className={cx('tips-text-title')}>
                    <Icon className={cx('icon')} name="warning-filled" />
                    <div>末端走线注意事项</div>
                  </div>
                  <div className={cx('tips-text-content')}>
                    <div className={cx('tips-text-box')}>
                      <span>1.</span>
                      <span>请按照图示检查线路的关键点位是否已固定。</span>
                    </div>

                    <div className={cx('tips-text-box', 'no-wrap')}>
                      <span>2.降末端线束</span>
                      <span className={cx('highlight')}>固定至卡扣内</span>
                      <span>，并左右锁紧卡扣防止线束拖落。</span>
                    </div>
                    <SuggestionsForFeedback
                      currentProcess="install-wire"
                      showTitle={false}
                      className={cx('feedback-wrapper')}
                    />
                  </div>
                </div>
                <div className={cx('tips-img', 'fix-end-wire-img')} />
              </div>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div className={cx('animate-card')}>
            <motion.div
              key="install"
              className="card"
              initial={{ opacity: 1, top: '2.5%', zIndex: 1000 }}
              animate={{ opacity: 1, top: '2.5%', zIndex: 1000 }}
              exit={{ opacity: 0.1, right: '-5%', transition: { duration: 1, delay: 0.2 } }}
            >
              <div className={cx('tips-box')}>
                <div className={cx('tips-text')}>
                  <div className={cx('tips-text-title')}>
                    <Icon className={cx('icon')} name="warning-filled" />
                    <div>安装理疗末端注意事项</div>
                  </div>
                  <div className={cx('tips-text-content')}>
                    {hasTipsForWipeMedium && (
                      <div className={cx('tips-text-box')}>
                        <span>1.</span>
                        <span>请在理疗区域涂抹介质</span>
                      </div>
                    )}
                    <div className={cx('tips-text-box')}>
                      <span>{hasTipsForWipeMedium ? '2' : '1'}.</span>
                      <span>
                        当红点对齐<span className={cx('highlight')}>红色“锁定图标”</span>
                        ，并听到“咔哒”一声后确认安装到位
                      </span>
                    </div>
                    <SuggestionsForFeedback
                      currentProcess="install-end-head"
                      showTitle={false}
                      className={cx('feedback-wrapper')}
                    />
                  </div>
                </div>
                <div className={cx('tips-img')}>
                  <FrameAnimation
                    className={cx('head-install-img')}
                    url={storagePrefixV2 + '/animation/head-lock-animation-min.png'}
                    aspectRatio={1.677}
                    count={173}
                    duration={4}
                    width={200}
                  />
                </div>
              </div>
              <div
                className={cx(
                  'dash-indicator',
                  { 'other-dash-indicator': ['Moxibustion', 'Massage']?.includes(currentTreatType) },
                  { 'is-matrix-mox': currentTreatType === 'MatrixMoxibustion' }
                )}
              >
                <img src={dashedCircleImg} alt="" className={cx('dashed-circle')} />
                <img src={dashedLineImg} alt="" className={cx('dashed-line')} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={cx('footer')}>
        <Button
          ref={endBtnRef}
          disabled={isDisconnectMachineArm}
          className={cx('btn-base')}
          loading={isStoping || massageState === 'Finishing'}
        >
          结束服务
        </Button>

        <Button ref={confirmBtn} disabled={confirmBtnDisabled} loading={confirmBtnLoading} className={cx('btn')} block>
          {massageState === 'Preparing_InstallingWiring' ? '确认固定末端走线' : '确认安装理疗末端'}
        </Button>
      </div>
    </div>
  );
};

export default InstallMassageHead;
